/**
 * File: usePushTraceController.ts
 * Path: apps/admin/src/features/diagnostics/hooks/usePushTraceController.ts
 * Description: The live notification test: sends one test notification, then follows it with timings (the
 * server sending it to each device, the bell saving it, each device confirming it was shown).
 */

import { useEffect, useState } from "react";
import { FunctionsService, type PushDevice } from "@idk/config";
import { toast } from "@idk/ui";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import { notificationsRepo } from "@/shared/services/notificationsService";
import { traceSteps, type SendResult, type TraceRun as Run } from "@/features/diagnostics/utils/traceUtils";

export const usePushTraceController = (devices: readonly PushDevice[]) => {
  const { user } = useAuth();
  const [run, setRun] = useState<Run | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!run || !user) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    const stop = notificationsRepo.watchItem(user.uid, run.itemId, (item) => {
      if (item) setSavedAt((t) => t ?? Date.now());
    });
    return () => {
      window.clearInterval(timer);
      stop();
    };
  }, [run, user]);

  const start = async () => {
    setSending(true);
    setSavedAt(null);
    try {
      const startedAt = Date.now();
      const result = await FunctionsService.call<undefined, { itemId: string; devices: SendResult[] }>(
        "sendTestNotification",
      );
      const answeredAt = Date.now();
      setNow(answeredAt);
      setRun({ startedAt, answeredAt, itemId: result.itemId, devices: result.devices });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't send the test.");
    } finally {
      setSending(false);
    }
  };

  const steps = run ? traceSteps(run, devices, savedAt, now) : [];
  return { hasRun: !!run, sending, steps, start };
};
